// Libs
import React from 'react';
import { Row, Col, Form, Modal } from 'react-bootstrap';
import moment from 'moment';
import ExpandingTextArea from 'react-expanding-textarea';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import BaseService from 'services/BaseService';
import BookingService from 'services/BookingService';
import InvoiceService from 'services/InvoiceService';
import BootboxHelper from 'helpers/BootboxHelper';
import DateHelpers from 'helpers/DateHelpers';

// Components
import Loader from 'components/common/Loader';
import DatePicker from 'components/common/DatePicker';

//-------------------------------------------------------------------------------------------------------------------

class BookingInvoiceModal extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            isUploading: false,
            file: null
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.bookingID = opts.bookingID;
            this.resolve = resolve;
            this.loginInfo = GlobalStateService.getValue('loginInfo');

            this.setState({
                isOpen: true,
                invoice: null,
                isLoading: true,
                step: '',
                invoiceValueIncorrectReason: ''
            });
            this.load();
        });
    }

    close() {
        this.setState({
            isOpen: false,
            invoice: null
        });
        if (this.resolveOnClose) {
            this.resolve();
            this.resolveOnClose = false;
        }
    }

    async load() {
        //return new Promise(async (resolve) => {
        const booking = await BookingService.get(this.bookingID);
        booking.oldVendorInvoiceReference = booking.vendorInvoiceReference;
        booking.updateMatchingInvoices = true;
        let invoice = await InvoiceService.getForBooking(this.bookingID);
        if (!invoice) {
            invoice = {
                currency: booking.currency
            };
        }
        if (!invoice.classDeliveryValue) {
            const { expectedValue } = await BookingService.getExpectedValue(this.bookingID);
            invoice.classDeliveryValue = expectedValue;
            invoice.totalValue = expectedValue;
        }
        this.setState({
            booking,
            invoice,
            file: (invoice.uploadFileName ? {
                name: invoice.uploadFileName
            } : null),
            isLoading: false
        });
    }

    async acceptFile(field) {
        const { invoice, booking } = this.state;
        if (field.files.length > 0 && !this.state.isUploading) {
            const file = field.files[0];
            this.setState({ isUploading: true, file });
            field.value = '';

            try {
                const { guid, fileName } = await BaseService.postFile('upload/add', file, { type: 'Invoice' });
                const { id, expectedPaymentDate } = await InvoiceService.upload(booking.id, guid, invoice.travelExpensesValue);
                this.setState({ isUploading: false });
                this.updateInvoice({
                    id,
                    uploadGUID: guid,
                    uploadFileName: fileName,
                    expectedPaymentDate
                });
                this.resolveOnClose = true;
            } catch (error) {
                BootboxHelper.alert(error);
                this.setState({
                    isUploading: false,
                    file: null
                });
            }
        }
    }

    async confirmMarkAsPaid() {
        const { vendorInvoiceReference, dateInvoicedVendor } = this.state;
        let confirm;

        // Check that vendor has been invoiced
        if (!vendorInvoiceReference || !dateInvoicedVendor) {
            confirm = await BootboxHelper.confirm('It looks like the vendor has not been invoiced yet (invoice reference and/or invoiced date are blank). Continue anyway?');
            if (!confirm) {
                return;
            }
        }

        confirm = await BootboxHelper.confirm('The instructor will be notified that payment has been released. Are you sure?');
        if (confirm) {
            this.markAsPaid();
            this.saveAndClose();
        }
    }

    async markAsPaid() {
        this.setState({
            isLoading: true
        });
        await InvoiceService.markAsPaid([ this.state.invoice.id ]);
        this.close();
        this.resolve();
    }

    async confirmArchive() {
        const confirm = await BootboxHelper.confirm('Archive this bill?');
        if (confirm) {
            this.archive();
            this.saveAndClose();
        }
    }

    async archive() {
        this.setState({ isLoading: true });
        await InvoiceService.archive(this.state.invoice.id);
        this.close();
        this.resolve();
    }

    async confirmIgnoreInvoice() {
        const confirm = await BootboxHelper.confirm('This will hide the booking from the instructor\'s pending invoice list and the bill list. Are you sure?');
        if (confirm) {
            this.setState({ isLoading: true });
            await BookingService.ignoreInvoice(this.state.booking.id);
            this.close();
            this.resolve();
        }
    }

    async saveAndClose() {
        this.setState({ isLoading: true });
        await InvoiceService.save(this.state.invoice, this.state.booking);
        this.close();
        this.resolve();
    }

    updateInvoice(newValues, callback) {
        const invoice = { ...this.state.invoice };
        for (let fieldName in newValues) {
            const value = newValues[fieldName];
            invoice[fieldName] = value;
        }
        invoice.totalValue = (Number(invoice.classDeliveryValue) || 0) + (Number(invoice.travelExpensesValue) || 0);
        this.setState({ invoice }, callback);
    }

    updateBookingField(field, value) {
        const booking = { ...this.state.booking };
        booking[field] = value;
        this.setState({
            booking
        });
    }

    async confirmUnsubmit() {
        const confirm = await BootboxHelper.confirm('Are you sure you want to delete this invoice and upload a new one?');
        if (confirm) {
            this.unsubmit();
        }
    }

    async unsubmit() {
        const { invoice, booking } = this.state;
        this.setState({ isLoading: true });
        if (invoice.id) {
            await InvoiceService.unsubmit(invoice.id);
        } else {
            await InvoiceService.notifyValueIncorrect(booking.id, null);
        }
        this.load();
        this.resolveOnClose = true;
    }

    async notifyValueIncorrect() {
        const { booking, invoiceValueIncorrectReason } = this.state;
        this.setState({ isLoading: true });
        await InvoiceService.notifyValueIncorrect(booking.id, invoiceValueIncorrectReason);
        this.updateBookingField('invoiceValueIncorrectReason', invoiceValueIncorrectReason);
        this.setState({ isLoading: false });
    }

    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isOpen, invoice
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (
            <Modal show className={`booking-invoice-modal ${invoice && invoice.id ? 'has-invoice' : ''}`}>
                {this.renderInner()}
            </Modal>
        );
    }

    renderInner() {
        const {
            isLoading,
            invoice
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader fullPage />
                </Modal.Body>
            );
        }

        return (<>

            <Modal.Body>

                <div className="d-flex">

                    <div className="flex-grow-1">

                        {this.renderBooking()}

                        {this.loginInfo.isAccountantOrSiteAdmin && this.renderAccountantView()}

                        {this.loginInfo.isInstructorOrManager && this.renderInstructorView()}

                    </div>

                    {invoice && !!invoice.id &&
                        <iframe src={`/api/upload/get/${invoice.uploadGUID}?isInline=true#toolbar=0&navpanes=0`} />
                    }

                </div>

            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                    Close
                </button>

                {this.loginInfo.isAccountantOrAdmin && <>
                    {!!invoice.id && !invoice.datePaid && !invoice.isOnHold &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.confirmMarkAsPaid()}>
                            Mark as Paid
                        </button>
                    }
                    {!!invoice.id &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.confirmArchive()}>
                            Archive
                        </button>
                    }
                    {!invoice.id &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.confirmIgnoreInvoice()}>
                            Skip invoicing
                        </button>
                    }
                    <button type="button" className="btn btn-primary ms-auto" onClick={() => this.saveAndClose()}>
                        Save
                    </button>
                </>}

            </Modal.Footer>
        </>);
    }

    renderBooking() {
        const {
            booking
        } = this.state;

        return (<>

            <h2>Booking</h2>

            <Row>

                <Col md="5">

                    <Form.Group className="">
                        <Form.Label>Course</Form.Label>
                        <p className="form-control-plaintext">
                            {booking.courseName}
                        </p>
                    </Form.Group>

                </Col>

                <Col md="2">

                    <Form.Group className="">
                        <Form.Label>Date(s)</Form.Label>
                        <div className="form-control-plaintext">
                            {booking.dateRuns.map((dr, index) =>
                                <div key={index}>
                                    {DateHelpers.formatDateRun(dr)}
                                </div>
                            )}
                        </div>
                    </Form.Group>

                </Col>

                <Col md="2">

                    <Form.Group className="">
                        <Form.Label>Duration</Form.Label>
                        <div className="form-control-plaintext">
                            {booking.numDays} day{booking.numDays >= 2 && 's'}
                        </div>
                    </Form.Group>

                </Col>

                <Col md="3">
                    <Form.Group className="">
                        <Form.Label>{booking.isATPBooking ? 'Booking ref' : 'LMS ID'}</Form.Label>
                        <p className="form-control-plaintext">
                            {booking.lmsid}
                        </p>
                    </Form.Group>
                </Col>

            </Row>

        </>);
    }

    renderAccountantView() {
        const { invoice, booking } = this.state;

        return (<>

            <h2>Instructor's bill</h2>

            <Row>

                <Col md="4">

                    <Form.Group className="">
                        <Form.Label>Status</Form.Label>
                        <p className="form-control-plaintext">
                            {invoice.datePaid ?
                                <>Paid on {moment(invoice.datePaid).format('DD/MM/YYYY')}</> :
                                <>Submitted on {moment(invoice.dateUploaded).format('DD/MM/YYYY')}</>
                            }
                        </p>
                    </Form.Group>

                </Col>
                
                <Col md="4">

                    <Form.Group className="">
                        <Form.Label>Value</Form.Label>
                        <p className="form-control-plaintext">
                            {invoice.classDeliveryValue} {invoice.currency && invoice.currency.code}
                            {invoice.travelExpensesValue > 0 && <>
                                <br />+ {invoice.travelExpensesValue} {invoice.currency && invoice.currency.code} (travel)
                                <br /><strong>= {invoice.totalValue} {invoice.currency && invoice.currency.code}</strong>
                            </>}
                        </p>
                    </Form.Group>

                </Col>

                <Col md="4">

                    {this.renderPDFViewDownload()}

                </Col>

                {this.renderOnHoldFields()}

            </Row>

            <h2>Invoice to vendor</h2>

            <Row>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Cost centre</Form.Label>
                        <p className="form-control-plaintext">
                            {booking.costCentre && booking.costCentre.name}
                        </p>
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>PO number</Form.Label>
                        <p className="form-control-plaintext">
                            {booking.costCentre && booking.costCentre.poNumber}
                        </p>
                    </Form.Group>

                </Col>

            </Row>

            <Row>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Our invoice ref</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={booking.vendorInvoiceReference || ''}
                            onChange={e => this.updateBookingField('vendorInvoiceReference', e.target.value)}
                        />
                        {booking.oldVendorInvoiceReference && booking.vendorInvoiceReference &&
                            (booking.oldVendorInvoiceReference != booking.vendorInvoiceReference) &&
                            <div className="form-check mt-3">
                                <Form.Label>
                                    <input
                                        type="checkbox"
                                        checked={booking.updateMatchingInvoices || false}
                                        onChange={e => this.updateBookingField('updateMatchingInvoices', e.target.checked)}
                                    />
                                    Update matching invoices
                                </Form.Label>
                            </div>
                        }
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Date invoiced vendor</Form.Label>
                        <DatePicker
                            className="form-control"
                            selected={booking.dateInvoicedVendor ? new Date(booking.dateInvoicedVendor) : null}
                            onChange={value => this.updateBookingField('dateInvoicedVendor', value)}
                            placeholder="Leave blank for 'today'"
                        />
                    </Form.Group>

                </Col>

                <Col md="4">

                    {booking.dateInvoicedVendor &&
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor paid</Form.Label>
                            <Form.Check
                                type="checkbox"
                                id="has-vendor-paid"
                                checked={!!booking.hasVendorPaid}
                                onChange={e => this.updateBookingField('hasVendorPaid', e.target.checked)}
                                label="Vendor paid"
                            />
                        </Form.Group>
                    }

                </Col>
            </Row>

            {booking.status == 'Cancelled' &&
                <div className="alert alert-danger text-bad text-center mt-2">
                    Class cancelled
                    {booking.cancelledInvoiceProportion > 0 ?
                        <>, billed at {booking.cancelledInvoiceProportion * 100}% as per T&amp;Cs</> :
                        <>, not billed</>
                    }
                </div>
            }

        </>);
    }

    renderPDFViewDownload() {
        const { invoice } = this.state;
        if (!invoice.id || !invoice.uploadFileName) {
            return null;
        }
        return (
            <Form.Group className="">
                <Form.Label>Uploaded PDF</Form.Label>
                <p className="form-control-plaintext">
                    <a className="btn btn-secondary" href={`/api/upload/get/${invoice.uploadGUID}?isInline=true`} target="_blank">
                        View
                    </a>
                    <a className="btn btn-secondary" href={`/api/upload/get/${invoice.uploadGUID}?isInline=false`}>
                        Download
                    </a>
                </p>
            </Form.Group>
        );
    }

    renderOnHoldFields() {
        const { invoice } = this.state;

        return (<>
            
            {this.loginInfo.isAdmin && invoice.id && !invoice.datePaid && <>

                <Form.Group className="mb-3">
                    <Form.Label>Bill on hold</Form.Label>
                    <Form.Check
                        type="checkbox"
                        id="is-on-hold"
                        checked={invoice.isOnHold}
                        onChange={e => this.updateInvoice({ isOnHold: e.target.checked })}
                        label="Bill on hold"
                    />
                </Form.Group>

                {invoice.isOnHold &&
                    <Form.Group className="">
                        <Form.Label>Reason</Form.Label>
                        <ExpandingTextArea
                            rows={3}
                            className="form-control"
                            value={invoice.onHoldReason || ''}
                            onChange={e => this.updateInvoice({ onHoldReason: e.target.value })}
                        />
                    </Form.Group>
                }

            </>}

            {this.loginInfo.isAccountant && invoice.isOnHold &&

                <div className="alert alert-danger">
                    This invoice is on hold; please do not pay it yet.
                    <br /><br />Reason: {invoice.onHoldReason}
                </div>

            }

        </>);
    }

    renderInstructorView() {
        const { invoice } = this.state;
        
        return (<>

            <h2>My bill</h2>
            
            {!!invoice.id ?
                this.renderSubmitted() :
                this.renderNotSubmitted()
            }

        </>);
    }

    renderSubmitted() {
        const { invoice } = this.state;
        
        return (<>

            <Row>

                <Col md="8">

                    <Form.Group className="">
                        <Form.Label>Status</Form.Label>
                        {invoice.datePaid ?
                            <div className="alert alert-success">
                                Your invoice was paid on {moment(invoice.datePaid).format('DD/MM/YYYY')}.
                            </div> :
                            <div className="alert alert-success">
                                Your invoice was submitted successfully and will be scheduled for payment.
                                {/* REQ Chris 19/12/2023
                                Thank you for submitting your invoice. <br />
                                The estimated payment date is {moment(invoice.expectedPaymentDate).format('DD/MM/YYYY')}.
                                */}
                            </div>
                        }
                    </Form.Group>

                    {!invoice.datePaid && <>
                        <p>
                            If you have uploaded the incorrect PDF by mistake or otherwise
                            need to make any amendments, please press the button below.
                        </p>

                        <button type="button" className="btn btn-primary" onClick={() => this.confirmUnsubmit()}>
                            Delete invoice and upload again
                        </button>
                    </>}

                </Col>

                <Col md="4">

                    {this.renderPDFViewDownload()}

                </Col>

            </Row>
        
        </>);
    }

    renderNotSubmitted() {
        const { step, invoice, booking, isUploading, invoiceValueIncorrectReason } = this.state;

        if (booking.invoiceValueIncorrectReason) {
            return (<>

                <div className="alert alert-danger">

                    You have told us that the value of this billing is incorrect.<br/>
                    <br/>
                    <strong>Reason:</strong> {booking.invoiceValueIncorrectReason}<br />
                    <br/>
                    We will get back to you as soon as possible.

                </div>
                
                <button type="button" className="btn btn-primary" onClick={() => this.confirmUnsubmit()}>
                    Delete invoice and upload again
                </button>

            </>);
        }

        switch (step || '') {
            case '':
                return (<>
                    
                    Please confirm that the pre-VAT value of the billing
                    {booking.hasTravelExpenses && <>{' '}(excluding travel expenses)</>}{' '}
                    is <strong>{invoice.classDeliveryValue} {invoice.currency && invoice.currency.code}</strong>.
                    {booking.status == 'Cancelled' && booking.cancelledInvoiceProportion < 1 && <>
                        <br />
                        <i>(As this class was cancelled, it must be billed at {booking.cancelledInvoiceProportion * 100}% of the standard value)</i>
                    </>}

                    <br />
                    <br />

                    <button type="button" className="btn btn-primary" onClick={() => this.setState({ step: (booking.hasTravelExpenses ? 'travel' : 'upload') })}>
                        <span className="fa fa-check" />{' '}
                        Yes - continue
                    </button>
                    {' '}
                    <button type="button" className="btn btn-secondary" onClick={() => this.setState({ step: 'value-incorrect' })}>
                        <span className="fa fa-times" />{' '}
                        No - contact us
                    </button>

                </>);

            case 'travel':
                return (<>

                    <Form.Group className="mb-3">
                        <Form.Label>Please let us know your total travel expenses</Form.Label>
                        <div className="input-group">
                            <input
                                type="number"
                                autoFocus
                                className="form-control"
                                value={invoice.travelExpensesValue || ''}
                                onChange={e => this.updateInvoice({ travelExpensesValue: e.target.value })}
                            />
                            {booking.currency &&
                                <div className="input-group-append">
                                    <span className="input-group-text">
                                        {booking.currency.code}
                                    </span>
                                </div>
                            }
                        </div>
                    </Form.Group>

                    <button type="button" className="btn btn-primary" onClick={() => this.setState({ step: 'upload' })}>
                        <span className="fa fa-check" />{' '}
                        Continue
                    </button>

                </>);

            case 'upload':
                
                return (<>

                    {booking.hasTravelExpenses && <>
                        <p>
                            <strong>Class delivery</strong>: {invoice.classDeliveryValue || 0} {invoice.currency && invoice.currency.code}
                        </p>
                        <p>
                            <strong>Travel expenses</strong>: {invoice.travelExpensesValue || 0} {invoice.currency && invoice.currency.code}
                        </p>
                    </>}
                    <p>
                        <strong>Total invoice value</strong>: {invoice.totalValue} {invoice.currency && invoice.currency.code}
                    </p>

                    If this is correct, please upload your invoice using the button below.

                    <div className="text-danger">
                        Note: the invoice you submit must contain this LMS ID only.
                    </div>

                    <div className="btn-stack mt-3">

                        <label className="btn btn-primary upload-button">
                            {isUploading ? <>
                                <Loader isInline />{' '}
                                Uploading...
                            </> : <>
                                <span className="fa-solid fa-file-upload"></span>{' '}
                                Upload your invoice PDF
                            </>}
                            <input type="file" accept=".pdf" onChange={e => this.acceptFile(e.target)} disabled={isUploading} />
                        </label>

                        {booking.hasTravelExpenses &&
                            <button type="button" className="btn btn-secondary" onClick={() => this.setState({ step: 'travel' })}>
                                <span className="fa fa-pencil" />{' '}
                                Amend travel expenses
                            </button>
                        }

                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ step: 'value-incorrect' })}>
                            <span className="fa fa-times" />{' '}
                            Class delivery value is incorrect
                        </button>

                    </div>

                </>);

            case 'value-incorrect':
                return (<>

                    <Form.Group className="mb-3">
                        <Form.Label>Please let us know why the billing amount is incorrect</Form.Label>
                        <ExpandingTextArea
                            className="form-control"
                            autoFocus
                            rows={3}
                            value={invoiceValueIncorrectReason || ''}
                            onChange={e => this.setState({ invoiceValueIncorrectReason: e.target.value })}
                        />
                    </Form.Group>

                    <button type="button" className="btn btn-primary" onClick={() => this.notifyValueIncorrect()}>
                        Submit
                    </button>

                </>);

        }
    }

}

export default BookingInvoiceModal;